import { Injectable } from '@angular/core';
import { OFFLINE_SERVICE_SW_URL_PATTERNS } from '@shared/constants/offline-route-precache.constants';

interface NgswManifestGroup {
  urls?: string[];
}

interface NgswManifest {
  assetGroups?: NgswManifestGroup[];
}

@Injectable({ providedIn: 'root' })
export class OfflineSwPrecacheService {
  async precacheOfflineServiceAssets(
    patterns: readonly RegExp[] = OFFLINE_SERVICE_SW_URL_PATTERNS,
  ): Promise<void> {
    if (!('serviceWorker' in navigator)) {
      return;
    }

    try {
      const manifest = await this.loadManifest();
      const urls = this.collectMatchingUrls(manifest, patterns);
      await this.precacheAssetUrls(urls);
    } catch {
      // SW precache is best-effort; IndexedDB data is the source of truth for form content.
    }
  }

  /** Fetches same-origin static assets so the ngsw lazy `assets` group can serve them offline. */
  async precacheAssetUrls(urls: readonly string[]): Promise<void> {
    if (!('serviceWorker' in navigator) || urls.length === 0) {
      return;
    }

    await Promise.all(urls.map((url) => this.fetchForServiceWorkerCache(url)));
  }

  private async loadManifest(): Promise<NgswManifest> {
    const manifestUrl = this.resolveManifestUrl();
    const response = await fetch(manifestUrl, { cache: 'no-cache' });
    if (!response.ok) {
      throw new Error('Unable to load ngsw.json');
    }

    return response.json() as Promise<NgswManifest>;
  }

  private resolveManifestUrl(): string {
    if (typeof document === 'undefined') {
      return 'ngsw.json';
    }

    return new URL('ngsw.json', document.baseURI).toString();
  }

  private collectMatchingUrls(manifest: NgswManifest, patterns: readonly RegExp[]): string[] {
    const urls = new Set<string>();

    for (const group of manifest.assetGroups ?? []) {
      for (const url of group.urls ?? []) {
        if (patterns.some((pattern) => pattern.test(url))) {
          urls.add(url);
        }
      }
    }

    return [...urls];
  }

  private async fetchForServiceWorkerCache(url: string): Promise<void> {
    try {
      const resolvedUrl = this.resolveAssetUrl(url);
      await fetch(resolvedUrl, { cache: 'default', credentials: 'same-origin' });
    } catch {
      // Ignore individual chunk failures during best-effort warming.
    }
  }

  private resolveAssetUrl(url: string): string {
    if (typeof document === 'undefined') {
      return url;
    }

    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }

    return new URL(url, document.baseURI).toString();
  }
}
